import React from 'react';

import PropTypes from 'prop-types';
import queryString from 'query-string';
import { renderRoutes } from 'react-router-config';

import WanderuErrorBoundary from 'wanderu-component-error-boundary';
import Footer from 'wanderu-component-footer';
import { Redirect } from 'wanderu-component-react-router-link';
import {
    i18nCoordinator,
} from 'wanderu-i18n';
import {
    scrollTo,
} from 'wanderu-utils';

import localesConfig from '../../../config/explorer.locales';
import ExplorerHeader from '../../components/ExplorerHeader/ExplorerHeader';
import ExplorerMeta from '../../components/ExplorerMeta/ExplorerMeta';
import events from '../../components/events';
import {
    setDate,
    setPrice,
} from '../../ducks/Filters';

const DEFAULT_LOCATION_TEXT = 'your-location';

const routePropType = {
    component: PropTypes.elementType,
    key: PropTypes.string,
    path: PropTypes.string,
};

class Explorer extends React.Component {
    static propTypes = {
        date: PropTypes.string,
        fetchCityFromPlaceId: PropTypes.func,
        locale: PropTypes.string,
        location: PropTypes.shape({
            pathname: PropTypes.string,
            search: PropTypes.string,
        }),
        match: PropTypes.shape({
            params: PropTypes.shape({
                countryState: PropTypes.string,
            }),
        }),
        originCities: PropTypes.shape({
            fetching: PropTypes.bool,
        }),
        originCity: PropTypes.shape({
            cityToCityIds: PropTypes.arrayOf(PropTypes.string),
            formattedAddress: PropTypes.string,
            placeId: PropTypes.string,
            url: PropTypes.string,
        }),
        placeId: PropTypes.string,
        price: PropTypes.number,
        route: PropTypes.shape({
            ...routePropType,
            routes: PropTypes.arrayOf(PropTypes.shape(routePropType)),
        }),
        setDate: PropTypes.func,
        setInvalidSearch: PropTypes.func,
        setOriginCityActive: PropTypes.func,
        setPrice: PropTypes.func,
        staticContext: PropTypes.shape({
            cache: PropTypes.shape({
                secondsFromNow: PropTypes.number,
            }),
            status: PropTypes.number,
            url: PropTypes.string,
        }),
        status: PropTypes.shape({
            redirectTo: PropTypes.shape({
                url: PropTypes.string,
            }),
        }),
        t: PropTypes.func,
    };

    static serverFetchData({ dispatch, i18nCoordinator: i18nServerCoordinator, query = {} }) {
        const {
            date,
            price,
        } = query;

        if (date) {
            dispatch(setDate(date));
        }

        if (price) {
            dispatch(setPrice(parseInt(price, 10)));
        }

        i18nServerCoordinator.registerTranslatableApp({
            ...localesConfig,
            translationFetchFn: (locale) => {
                return import(`../../translations/explorer/${locale}.po`);
            },
        });
    }

    constructor(props) {
        super(props);
        const { originCity } = this.props;

        this.state = {
            input: originCity?.formattedAddress,
            inputFocused: false,
            placeId: originCity?.placeId,
            renderRedirect: false,
        };
    }

    componentDidMount() {
        const {
            fetchCityFromPlaceId,
            placeId,
        } = this.props;

        if (placeId) {
            fetchCityFromPlaceId(placeId);
        }

        i18nCoordinator.registerTranslatableApp({
            ...localesConfig,
            translationFetchFn: (locale) => {
                return import(`../../translations/explorer/${locale}.po`);
            },
        });
    }

    componentDidUpdate(prevProps) {
        const {
            location,
            originCities,
            originCity,
        } = this.props;

        if (!originCities?.fetching && prevProps?.originCities?.fetching) {
            this.setState(
                () => ({ renderRedirect: true }),
                () => this.setState(() => ({ renderRedirect: false })),
            );
        }

        if (location?.search !== prevProps?.location?.search) {
            const queries = queryString.parse(location?.search);
            this.updateFilters(queries);
        }

        const {
            formattedAddress,
            placeId,
        } = originCity;

        if (placeId !== prevProps?.originCity?.placeId) {
            this.setState(() => ({ placeId }));
        }

        if (!formattedAddress) {
            return;
        }

        if (formattedAddress !== prevProps?.originCity?.formattedAddress) {
            this.setState(() => ({ input: originCity?.formattedAddress }));
        }
    }

    // eslint-disable-next-line class-methods-use-this
    componentWillUnmount() {
        i18nCoordinator.unregisterTranslatableApp(localesConfig.namespace);
    }

    updateFilters(queries) {
        if (!queries) {
            return;
        }

        const {
            date,
            price,
        } = this.props;

        if (queries?.date !== date) {
            this.props.setDate(queries?.date);
        }

        if (queries?.price !== price) {
            this.props.setPrice(parseInt(queries?.price, 10));
        }
    }

    onInputChange = (e) => {
        const input = e?.target?.value;
        this.setState(() => ({ input }));
    }

    onInputBlur = (e) => {
        if (this.state.input) {
            return;
        }

        const { formattedAddress } = this.props.originCity;

        this.setState(() => ({
            input: formattedAddress,
            inputFocused: false,
        }));
    }

    onInputFocus = (e) => {
        this.setState(() => ({
            inputFocused: true,
        }));
    }

    onDateChange = (e) => {
        let date = e?.target?.value;
        if (date) {
            // not localized - this goes in the url
            date = date.format('YYYY-MM-DD');
        }

        this.props.setDate(date);
    }

    onPriceChange = (e) => {
        const price = e?.target?.value;
        this.props.setPrice(price);
    }

    onLocationChange = (e) => {
        const placeId = e?.target?.value;
        if (!placeId) {
            return;
        }

        this.setState(() => ({ placeId }));
    }

    onSearch = () => {
        const {
            date: currDate,
            fetchCityFromPlaceId,
            location,
            originCity,
            price: currPrice,
            setInvalidSearch,
        } = this.props;

        events.clickSearch({ date: currDate, origin: this.state.input, price: currPrice });

        const { placeId } = this.state;
        if (!placeId) {
            return setInvalidSearch();
        }

        const { date: oldDate } = queryString.parse(location.search);
        let { price: oldPrice } = queryString.parse(location.search);

        oldPrice = parseInt(oldPrice ?? 0, 10);

        const samePlace = placeId === originCity?.placeId;
        const sameDate = oldDate === currDate;
        const samePrice = oldPrice === currPrice;

        // fetch only if a field other than price has been updated
        if (!samePlace || !sameDate) {
            return fetchCityFromPlaceId(placeId);
        }

        if (samePrice) {
            return scrollTo(500, { duration: 750 });
        }

        return this.setState(
            () => ({ renderRedirect: true }),
            () => this.setState(() => ({ renderRedirect: false })),
        );
    }

    onNearbyCityClick = (e, nearbyId) => {
        this.props.setOriginCityActive(nearbyId);
    }

    render() {
        const {
            date,
            locale,
            match,
            originCity,
            price,
            route,
            staticContext,
            status,
            t,
        } = this.props;
        const {
            countryState,
        } = match.params;

        let {
            input,
            inputFocused,
            renderRedirect,
        } = this.state;

        if (staticContext && !countryState) {
            staticContext.cache = {
                secondsFromNow: 60 * 60 * 24 * 7, // a week
            };
        }

        if (!input && !inputFocused) {
            input = t(DEFAULT_LOCATION_TEXT);
        }

        if (renderRedirect && !!originCity?.url) {
            return (
                <Redirect
                    href={`/explore/${originCity?.url}/`}
                    queries={{
                        date,
                        placeId: originCity?.placeId,
                        price,
                    }}
                />
            );
        }

        if (renderRedirect && status?.redirectTo) {
            return (
                <Redirect
                    href={status?.redirectTo?.url}
                    queries={{
                        date,
                        price,
                    }}
                />
            );
        }

        return (
            <WanderuErrorBoundary scope="ExplorerContainer">
                <ExplorerMeta
                    noFollow={!!countryState}
                    noIndex={!!countryState}
                />
                <ExplorerHeader
                    date={date}
                    hideResults={!originCity}
                    input={input}
                    locale={locale}
                    onDateChange={this.onDateChange}
                    onInputBlur={this.onInputBlur}
                    onInputChange={this.onInputChange}
                    onInputFocus={this.onInputFocus}
                    onLocationChange={this.onLocationChange}
                    onNearbyCityClick={this.onNearbyCityClick}
                    onPriceChange={this.onPriceChange}
                    onSearch={this.onSearch}
                    price={price}
                    status={status}
                />
                {renderRoutes(route?.routes)}
                <Footer />
            </WanderuErrorBoundary>
        );
    }
}

export { Explorer };
